import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Settings2,
  Bot,
  ShieldCheck,
  Ticket,
  TrendingUp,
  ScrollText,
  MousePointerClick,
  Cake,
  Mic,
  Activity as ActivityIcon,
  Users,
  Hash,
  Shield,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { useApi } from "@/hooks/useApi";
import {
  getGuild,
  getGuildChannels,
  getGuildRoles,
  getGuildConfig,
  getGuildActivity,
  getAssignableRoles,
  updateGuildConfig,
} from "@/api/guilds";
import { guildIconUrl, inviteForGuild } from "@/api/discordAssets";
import { DISCORD_INVITE_URL } from "@/config";
import { GlassCard } from "@/components/GlassCard";
import { OfflineState, ErrorState, EmptyState } from "@/components/States";
import { SignInPrompt } from "./Dashboard";
import {
  Toggle,
  ToggleRow,
  Field,
  TextInput,
  Textarea,
  NumInput,
  ColorInput,
  ChannelSelect,
  RoleSelect,
  ChoiceSelect,
  ModuleCard,
  ReadOnlyNote,
  SectionNav,
  FadeIn,
} from "@/components/dashboard/fields";
import { AutoResponders } from "@/components/dashboard/AutoResponders";
import { AutoReacts } from "@/components/dashboard/AutoReacts";
import { Autoroles } from "@/components/dashboard/Autoroles";
import { ReactionRoles } from "@/components/dashboard/ReactionRoles";
import { AntinukeLists } from "@/components/dashboard/AntinukeLists";
import { AutomodWhitelist } from "@/components/dashboard/AutomodWhitelist";
import { BotProfile } from "@/components/dashboard/BotProfile";
import { EmbedBuilder } from "@/components/dashboard/EmbedBuilder";
import { WelcomeGoodbye } from "@/components/dashboard/WelcomeGoodbye";
import { TicketsManager } from "@/components/dashboard/TicketsManager";

const SECTIONS = [
  { id: "general", label: "General" },
  { id: "profile", label: "Bot Profile" },
  { id: "autoresponders", label: "Responders" },
  { id: "autoreacts", label: "Reacts" },
  { id: "autoroles", label: "Autorole" },
  { id: "reaction-roles", label: "Reaction Roles" },
  { id: "automod", label: "Automod" },
  { id: "antinuke", label: "Anti-Nuke" },
  { id: "tickets", label: "Tickets" },
  { id: "leveling", label: "Leveling" },
  { id: "welcome", label: "Welcome" },
  { id: "goodbye", label: "Goodbye" },
  { id: "embeds", label: "Embeds" },
  { id: "logging", label: "Logging" },
  { id: "invites", label: "Invites" },
  { id: "birthday", label: "Birthday" },
  { id: "voice", label: "Voice" },
  { id: "activity", label: "Activity" },
];

const PUNISHMENTS = ["delete", "warn", "mute", "kick", "ban"];
const ANTINUKE_PUNISHMENTS = ["ban", "kick", "strip", "mute", "warn"];

const AUTOMOD_MODULES = [
  { key: "antilink", label: "Anti-Link", description: "Deletes messages that contain links." },
  { key: "antiinvite", label: "Anti-Invite", description: "Blocks Discord server invite links." },
  { key: "antispam", label: "Anti-Spam", description: "Stops repeated or flooded messages." },
  { key: "antimention", label: "Anti-Mention Spam", description: "Limits mass user and role mentions." },
  { key: "anticaps", label: "Anti-Caps", description: "Blocks messages written in excessive capital letters." },
  { key: "antiwordfilter", label: "Word Filter", description: "Deletes messages containing blacklisted words." },
  { key: "antiemoji", label: "Anti-Emoji Spam", description: "Limits emoji flooding in messages." },
  { key: "antizalgo", label: "Anti-Zalgo", description: "Blocks glitch text made of combining characters." },
  { key: "antinewline", label: "Anti-Newline Spam", description: "Blocks messages that abuse line breaks." },
  { key: "antiduplicate", label: "Anti-Duplicate", description: "Deletes the same message sent repeatedly." },
];

const MODULE_NAME_KEYS = ["module", "name", "key", "type"];

const moduleKeyOf = (item, index) => {
  if (item && typeof item === "object") {
    for (const k of MODULE_NAME_KEYS) {
      const v = item[k];
      if (typeof v === "string" && v && !/^\d+$/.test(v)) return v.toLowerCase();
    }
  }
  return AUTOMOD_MODULES[index]?.key;
};

// The API returns automod modules as a list of DB rows, but only writes them
// back when they are sent as a name-keyed object — normalize so edits persist.
const normalizeAutomodModules = (raw) => {
  const map = {};
  if (Array.isArray(raw)) {
    raw.forEach((item, i) => {
      const key = moduleKeyOf(item, i);
      if (key) map[key] = item && typeof item === "object" ? item : {};
    });
  } else if (raw && typeof raw === "object") {
    Object.assign(map, raw);
  }
  return map;
};

const normalizeConfig = (cfg) =>
  cfg?.automod
    ? { ...cfg, automod: { ...cfg.automod, modules: normalizeAutomodModules(cfg.automod.modules) } }
    : cfg;
const TEXT_CHANNELS = ["text", "news"];
const VOICE_CHANNELS = ["voice", "stage_voice"];

const StatChip = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3.5 py-1.5 font-mono text-xs text-paper-400">
    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    {label}
  </span>
);

export default function ServerManage() {
  const { guildId } = useParams();
  useSEO({ title: "Manage Server", description: "Configure Xevon for your server.", path: `/dashboard/servers/${guildId}`, noindex: true });

  const fetcher = useMemo(
    () => async () => {
      const [guild, channels, roles, configRes, activityRes, assignableRes] = await Promise.all([
        getGuild(guildId),
        getGuildChannels(guildId),
        getGuildRoles(guildId),
        getGuildConfig(guildId),
        getGuildActivity(guildId, 20),
        getAssignableRoles(guildId).catch(() => null),
      ]);
      return {
        guild,
        channels: channels.channels || [],
        roles: roles.roles || [],
        assignableRoles: assignableRes?.roles || null,
        config: configRes.config || {},
        activity: activityRes.activity || [],
      };
    },
    [guildId]
  );

  const { data, loading, error, refetch } = useApi(fetcher);

  const [config, setConfig] = useState(null);
  const [saved, setSaved] = useState(null);
  const [savingModule, setSavingModule] = useState(null);
  const [activeSection, setActiveSection] = useState("general");

  useEffect(() => {
    if (data && config === null) {
      const normalized = normalizeConfig(data.config);
      setConfig(normalized);
      setSaved(normalized);
    }
  }, [data, config]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveSection(e.target.id)),
      { rootMargin: "-25% 0px -65% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [data]);

  const setSection = (key, patch) => setConfig((c) => ({ ...c, [key]: { ...c[key], ...patch } }));
  const setNested = (key, sub, patch) =>
    setConfig((c) => ({ ...c, [key]: { ...c[key], [sub]: { ...(c[key]?.[sub] || {}), ...patch } } }));
  const setAutomodModule = (mod, patch) =>
    setConfig((c) => ({
      ...c,
      automod: {
        ...c.automod,
        modules: { ...(c.automod?.modules || {}), [mod]: { ...(c.automod?.modules?.[mod] || {}), ...patch } },
      },
    }));

  const isDirty = (key) =>
    key === "prefix" ? config?.prefix !== saved?.prefix : JSON.stringify(config?.[key]) !== JSON.stringify(saved?.[key]);

  const saveSection = async (key) => {
    setSavingModule(key);
    try {
      const payload = key === "prefix" ? { prefix: config.prefix } : { [key]: config[key] };
      const res = await updateGuildConfig(guildId, payload);
      const normalized = normalizeConfig(res.config);
      setSaved(normalized);
      setConfig(normalized);
      toast.success("Settings saved to Xevon.");
    } catch (e) {
      if (e.kind === "auth") toast.error("Your session expired. Please sign in again.");
      else if (e.kind === "offline" || e.kind === "timeout") toast.error("Unable to connect to Xevon services. Please try again later.");
      else toast.error("Could not save settings. Please try again.");
    } finally {
      setSavingModule(null);
    }
  };

  const notInstalled = error && error.kind === "http" && error.status === 404;

  const cardProps = (key) => ({
    dirty: isDirty(key),
    saving: savingModule === key,
    onSave: () => saveSection(key),
  });

  const c = config || {};
  const channels = data?.channels || [];
  const roles = data?.roles || [];
  const assignableRoles = data?.assignableRoles || roles.filter((r) => !r.managed && r.name !== "@everyone");

  return (
    <section className="mx-auto w-full max-w-5xl px-6 pb-32 pt-28 md:pt-36" data-testid="server-manage-page">
      <Link
        to="/dashboard"
        data-testid="back-to-dashboard"
        className="group inline-flex items-center gap-2 text-sm font-semibold text-paper-400 transition-colors duration-300 hover:text-paper-100"
      >
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" aria-hidden="true" />
        Back to dashboard
      </Link>

      {loading && (
        <div className="mt-8 space-y-4" data-testid="server-loading" role="status" aria-label="Loading server">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-2xl border border-white/5 bg-white/[0.03]" />
          ))}
        </div>
      )}

      {!loading && error && error.kind === "auth" && <div className="mt-8"><SignInPrompt testId="server-signin-prompt" /></div>}
      {!loading && error && (error.kind === "offline" || error.kind === "timeout") && <div className="mt-8"><OfflineState onRetry={refetch} /></div>}
      {!loading && notInstalled && (
        <div className="mt-8">
          <EmptyState title="Xevon isn't in this server yet." message="Add Xevon to unlock configuration, modules, and activity for this server." />
          <div className="mt-6 text-center">
            <a
              href={inviteForGuild(DISCORD_INVITE_URL, guildId)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="server-install-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.04]"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add Xevon to this server
            </a>
          </div>
        </div>
      )}
      {!loading && error && !["auth", "offline", "timeout"].includes(error.kind) && !notInstalled && (
        <div className="mt-8"><ErrorState onRetry={refetch} /></div>
      )}

      {!loading && data && config && (
        <>
          <FadeIn>
            <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-5">
                {guildIconUrl(data.guild) ? (
                  <img
                    src={guildIconUrl(data.guild)}
                    alt={`${data.guild.name} icon`}
                    width={64}
                    height={64}
                    decoding="async"
                    className="h-16 w-16 rounded-2xl border border-white/15 object-cover"
                    data-testid="server-icon"
                  />
                ) : (
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] font-display text-2xl font-semibold text-paper-200" aria-hidden="true">
                    {(data.guild.name || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Server configuration</p>
                  <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight text-paper-100 md:text-3xl" data-testid="server-name">
                    {data.guild.name}
                  </h1>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {typeof data.guild.member_count === "number" && (
                  <StatChip icon={Users} label={`${data.guild.member_count.toLocaleString("en-US")} members`} />
                )}
                {typeof data.guild.channels === "number" && <StatChip icon={Hash} label={`${data.guild.channels} channels`} />}
                {typeof data.guild.roles === "number" && <StatChip icon={Shield} label={`${data.guild.roles} roles`} />}
              </div>
            </div>
          </FadeIn>

          <div className="mt-10">
            <SectionNav sections={SECTIONS} active={activeSection} />
          </div>

          <div className="space-y-6">
            <FadeIn>
              <ModuleCard icon={Settings2} title="General" description="Server prefix and Xevon's profile inside this server. Changes write straight to the bot's database." id="general" testId="module-general" {...cardProps("prefix")}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Command prefix" hint="Used for prefix commands. Slash commands always work.">
                    <TextInput value={c.prefix} onChange={(v) => setConfig((x) => ({ ...x, prefix: v }))} placeholder="?" testId="general-prefix" />
                  </Field>
                </div>
                <ReadOnlyNote testId="general-readonly">
                  {c.autoreponders?.length || 0} auto responders · {c.autoreacts?.length || 0} auto reacts · {c.reaction_roles?.messages?.length || 0} reaction role messages — managed with bot commands.
                </ReadOnlyNote>
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <BotProfile guildId={guildId} />
            </FadeIn>

            <FadeIn>
              <AutoResponders guildId={guildId} />
            </FadeIn>

            <FadeIn>
              <AutoReacts guildId={guildId} />
            </FadeIn>

            <FadeIn>
              <Autoroles guildId={guildId} roles={assignableRoles} />
            </FadeIn>

            <FadeIn>
              <ReactionRoles guildId={guildId} roles={assignableRoles} channels={channels} />
            </FadeIn>

            <FadeIn>
              <ModuleCard icon={Bot} title="Automod" description="Automatic filters that keep chats clean — writes to the live automod tables." id="automod" testId="module-automod" {...cardProps("automod")}>
                <ToggleRow label="Enable automod" checked={!!c.automod?.enabled} onChange={(v) => setSection("automod", { enabled: v })} testId="automod-enabled" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <ToggleRow label="Anti-links" checked={!!c.automod?.antilink} onChange={(v) => setSection("automod", { antilink: v })} testId="automod-antilink" />
                  <ToggleRow label="Anti-invites" checked={!!c.automod?.antiinvite} onChange={(v) => setSection("automod", { antiinvite: v })} testId="automod-antiinvite" />
                  <ToggleRow label="Anti-spam" checked={!!c.automod?.antispam} onChange={(v) => setSection("automod", { antispam: v })} testId="automod-antispam" />
                  <ToggleRow label="Anti mention spam" checked={!!c.automod?.antimention} onChange={(v) => setSection("automod", { antimention: v })} testId="automod-antimention" />
                </div>
                <Field label="Automod log channel">
                  <ChannelSelect value={c.automod?.log_channel_id} onChange={(v) => setSection("automod", { log_channel_id: v })} channels={channels} types={TEXT_CHANNELS} testId="automod-log-channel" />
                </Field>

                <div className="space-y-3 rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="automod-modules">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Filter modules</p>
                    <span className="font-mono text-[10px] text-paper-500" data-testid="automod-modules-count">
                      {AUTOMOD_MODULES.filter((m) => c.automod?.modules?.[m.key]?.enabled).length}/{AUTOMOD_MODULES.length} active
                    </span>
                  </div>
                  {AUTOMOD_MODULES.map((m) => {
                    const settings = c.automod?.modules?.[m.key] || {};
                    return (
                      <div key={m.key} className="rounded-xl border border-white/5 p-3.5" data-testid={`automod-module-${m.key}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-paper-100">{m.label}</p>
                            <p className="mt-0.5 text-xs leading-relaxed text-paper-500">{m.description}</p>
                          </div>
                          <Toggle
                            checked={!!settings.enabled}
                            onChange={(v) => setAutomodModule(m.key, { enabled: v })}
                            label={`${m.label} enabled`}
                            testId={`automod-module-${m.key}-enabled`}
                          />
                        </div>
                        <div className="mt-3">
                          <Field label="Punishment">
                            <ChoiceSelect
                              value={settings.punishment}
                              onChange={(v) => setAutomodModule(m.key, { punishment: v })}
                              options={PUNISHMENTS}
                              testId={`automod-module-${m.key}-punishment`}
                            />
                          </Field>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-3 rounded-xl border border-white/5 bg-white/[0.02] p-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-paper-500">Warn escalation</p>
                  <ToggleRow label="Enable warn escalation" checked={!!c.automod?.warn?.enabled} onChange={(v) => setNested("automod", "warn", { enabled: v })} testId="automod-warn-enabled" />
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Threshold">
                      <NumInput value={c.automod?.warn?.threshold} onChange={(v) => setNested("automod", "warn", { threshold: v })} testId="automod-warn-threshold" />
                    </Field>
                    <Field label="Punishment">
                      <ChoiceSelect value={c.automod?.warn?.punishment} onChange={(v) => setNested("automod", "warn", { punishment: v })} options={["mute", "kick", "ban"]} testId="automod-warn-punishment" />
                    </Field>
                    <Field label="Duration (s)">
                      <NumInput value={c.automod?.warn?.duration} onChange={(v) => setNested("automod", "warn", { duration: v })} testId="automod-warn-duration" />
                    </Field>
                  </div>
                </div>

                <AutomodWhitelist guildId={guildId} roles={roles} channels={channels} modules={AUTOMOD_MODULES.map((m) => m.key)} />
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <ModuleCard icon={ShieldCheck} title="Anti-Nuke" description="Defense against raids and destructive mass actions." id="antinuke" testId="module-antinuke" {...cardProps("antinuke")}>
                <ToggleRow label="Enable anti-nuke" checked={!!c.antinuke?.enabled} onChange={(v) => setSection("antinuke", { enabled: v })} testId="antinuke-enabled" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Log channel">
                    <ChannelSelect value={c.antinuke?.log_channel_id} onChange={(v) => setSection("antinuke", { log_channel_id: v })} channels={channels} types={TEXT_CHANNELS} testId="antinuke-log-channel" />
                  </Field>
                  <Field label="Default punishment">
                    <ChoiceSelect value={c.antinuke?.default_punishment} onChange={(v) => setSection("antinuke", { default_punishment: v })} options={ANTINUKE_PUNISHMENTS} testId="antinuke-punishment" />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ToggleRow label="Ignore bots" checked={!!c.antinuke?.ignore_bots} onChange={(v) => setSection("antinuke", { ignore_bots: v })} testId="antinuke-ignore-bots" />
                  <ToggleRow label="Ignore owner" checked={!!c.antinuke?.ignore_owner} onChange={(v) => setSection("antinuke", { ignore_owner: v })} testId="antinuke-ignore-owner" />
                  <ToggleRow label="Ignore admins" checked={!!c.antinuke?.ignore_admins} onChange={(v) => setSection("antinuke", { ignore_admins: v })} testId="antinuke-ignore-admins" />
                  <ToggleRow label="DM on punishment" checked={!!c.antinuke?.punishment_dm} onChange={(v) => setSection("antinuke", { punishment_dm: v })} testId="antinuke-punishment-dm" />
                  <ToggleRow label="Auto-restore" checked={!!c.antinuke?.auto_restore} onChange={(v) => setSection("antinuke", { auto_restore: v })} testId="antinuke-auto-restore" />
                  <ToggleRow label="Danger mode" checked={!!c.antinuke?.danger_mode} onChange={(v) => setSection("antinuke", { danger_mode: v })} testId="antinuke-danger-mode" />
                </div>
                <AntinukeLists guildId={guildId} channels={channels} />
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <TicketsManager guildId={guildId} channels={channels} roles={assignableRoles} />
            </FadeIn>

            <FadeIn>
              <ModuleCard icon={TrendingUp} title="Leveling" description="XP rates, announcements, and reward behavior." id="leveling" testId="module-leveling" {...cardProps("leveling")}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <ToggleRow label="Enable leveling" checked={!!c.leveling?.settings?.enabled} onChange={(v) => setNested("leveling", "settings", { enabled: v })} testId="leveling-enabled" />
                  <ToggleRow label="Announce level-ups" checked={!!c.leveling?.settings?.announce} onChange={(v) => setNested("leveling", "settings", { announce: v })} testId="leveling-announce" />
                  <ToggleRow label="Stack level roles" checked={!!c.leveling?.settings?.reward_stack} onChange={(v) => setNested("leveling", "settings", { reward_stack: v })} testId="leveling-reward-stack" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Level-up channel">
                    <ChannelSelect value={c.leveling?.settings?.channel_id} onChange={(v) => setNested("leveling", "settings", { channel_id: v })} channels={channels} types={TEXT_CHANNELS} testId="leveling-channel" />
                  </Field>
                  <Field label="Level-up message">
                    <TextInput value={c.leveling?.settings?.message} onChange={(v) => setNested("leveling", "settings", { message: v })} placeholder="{user} reached level {level}!" testId="leveling-message" />
                  </Field>
                  <Field label="XP min">
                    <NumInput value={c.leveling?.settings?.xp_min} onChange={(v) => setNested("leveling", "settings", { xp_min: v })} testId="leveling-xp-min" />
                  </Field>
                  <Field label="XP max">
                    <NumInput value={c.leveling?.settings?.xp_max} onChange={(v) => setNested("leveling", "settings", { xp_max: v })} testId="leveling-xp-max" />
                  </Field>
                  <Field label="XP cooldown (s)">
                    <NumInput value={c.leveling?.settings?.xp_cooldown} onChange={(v) => setNested("leveling", "settings", { xp_cooldown: v })} testId="leveling-xp-cooldown" />
                  </Field>
                  <Field label="XP multiplier">
                    <NumInput value={c.leveling?.settings?.multiplier} onChange={(v) => setNested("leveling", "settings", { multiplier: v })} testId="leveling-multiplier" />
                  </Field>
                </div>
                <ReadOnlyNote testId="leveling-readonly">
                  {c.leveling?.rewards?.length || 0} level role rewards · {c.leveling?.blacklist?.length || 0} blacklisted channels/roles — manage with /level role and /level blacklist.
                </ReadOnlyNote>
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <WelcomeGoodbye.WelcomeCard guildId={guildId} channels={channels} />
            </FadeIn>

            <FadeIn>
              <WelcomeGoodbye.GoodbyeCard guildId={guildId} channels={channels} roles={assignableRoles} />
            </FadeIn>

            <FadeIn>
              <EmbedBuilder guildId={guildId} channels={channels} roles={assignableRoles} />
            </FadeIn>

            <FadeIn>
              <ModuleCard icon={ScrollText} title="Logging" description="Route server events to log channels." id="logging" testId="module-logging" {...cardProps("logging")}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Message logs">
                    <ChannelSelect value={c.logging?.message_logs} onChange={(v) => setSection("logging", { message_logs: v })} channels={channels} types={TEXT_CHANNELS} testId="logging-message" />
                  </Field>
                  <Field label="Member logs">
                    <ChannelSelect value={c.logging?.member_logs} onChange={(v) => setSection("logging", { member_logs: v })} channels={channels} types={TEXT_CHANNELS} testId="logging-member" />
                  </Field>
                  <Field label="Moderation logs">
                    <ChannelSelect value={c.logging?.moderation_logs} onChange={(v) => setSection("logging", { moderation_logs: v })} channels={channels} types={TEXT_CHANNELS} testId="logging-moderation" />
                  </Field>
                  <Field label="Role logs">
                    <ChannelSelect value={c.logging?.role_logs} onChange={(v) => setSection("logging", { role_logs: v })} channels={channels} types={TEXT_CHANNELS} testId="logging-role" />
                  </Field>
                  <Field label="Voice logs">
                    <ChannelSelect value={c.logging?.voice_logs} onChange={(v) => setSection("logging", { voice_logs: v })} channels={channels} types={TEXT_CHANNELS} testId="logging-voice" />
                  </Field>
                  <Field label="Server logs">
                    <ChannelSelect value={c.logging?.server_logs} onChange={(v) => setSection("logging", { server_logs: v })} channels={channels} types={TEXT_CHANNELS} testId="logging-server" />
                  </Field>
                </div>
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <ModuleCard icon={MousePointerClick} title="Invite Tracking" description="Track invites, leaves, rejoins, and fake accounts." id="invites" testId="module-invites" {...cardProps("invites")}>
                <ToggleRow label="Enable invite tracking" checked={!!c.invites?.enabled} onChange={(v) => setSection("invites", { enabled: v })} testId="invites-enabled" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Log channel">
                    <ChannelSelect value={c.invites?.log_channel_id} onChange={(v) => setSection("invites", { log_channel_id: v })} channels={channels} types={TEXT_CHANNELS} testId="invites-log-channel" />
                  </Field>
                  <Field label="Fake account threshold (days)">
                    <NumInput value={c.invites?.fake_threshold} onChange={(v) => setSection("invites", { fake_threshold: v })} testId="invites-fake-threshold" />
                  </Field>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <ToggleRow label="Track leaves" checked={!!c.invites?.track_leaves} onChange={(v) => setSection("invites", { track_leaves: v })} testId="invites-track-leaves" />
                  <ToggleRow label="Track rejoins" checked={!!c.invites?.track_rejoins} onChange={(v) => setSection("invites", { track_rejoins: v })} testId="invites-track-rejoins" />
                  <ToggleRow label="Track fakes" checked={!!c.invites?.track_fake} onChange={(v) => setSection("invites", { track_fake: v })} testId="invites-track-fake" />
                </div>
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <ModuleCard icon={Cake} title="Birthday" description="Celebrate member birthdays automatically." id="birthday" testId="module-birthday" {...cardProps("birthday")}>
                <ToggleRow label="Enable birthdays" checked={!!c.birthday?.enabled} onChange={(v) => setSection("birthday", { enabled: v })} testId="birthday-enabled" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Announcement channel">
                    <ChannelSelect value={c.birthday?.channel_id} onChange={(v) => setSection("birthday", { channel_id: v })} channels={channels} types={TEXT_CHANNELS} testId="birthday-channel" />
                  </Field>
                  <Field label="Birthday role">
                    <RoleSelect value={c.birthday?.role_id} onChange={(v) => setSection("birthday", { role_id: v })} roles={roles} testId="birthday-role" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Message" hint="Line breaks are kept — {user} or {mention} pings the birthday member.">
                      <Textarea value={c.birthday?.message} onChange={(v) => setSection("birthday", { message: v })} placeholder="Happy birthday {user}!" rows={4} testId="birthday-message" />
                    </Field>
                  </div>
                  <Field label="Timezone">
                    <TextInput value={c.birthday?.timezone} onChange={(v) => setSection("birthday", { timezone: v })} placeholder="UTC" testId="birthday-timezone" />
                  </Field>
                </div>
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <ModuleCard icon={Mic} title="Voice" description="Join to Create — members get their own temporary voice rooms." id="voice" testId="module-voice" {...cardProps("voice")}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Join to Create channel" hint="Pick a voice channel to enable, or leave unset to disable.">
                    <ChannelSelect
                      value={c.voice?.join_to_create?.channel_id}
                      onChange={(v) => setSection("voice", { join_to_create: v ? { ...(c.voice?.join_to_create || {}), channel_id: v } : { channel_id: null } })}
                      channels={channels}
                      types={VOICE_CHANNELS}
                      testId="voice-jtc-channel"
                    />
                  </Field>
                  <Field label="Category for new rooms">
                    <ChannelSelect
                      value={c.voice?.join_to_create?.category_id}
                      onChange={(v) => setSection("voice", { join_to_create: { ...(c.voice?.join_to_create || {}), channel_id: c.voice?.join_to_create?.channel_id, category_id: v } })}
                      channels={channels}
                      types={["category"]}
                      testId="voice-jtc-category"
                    />
                  </Field>
                </div>
                <ReadOnlyNote testId="voice-readonly">
                  {c.voice?.voicemaster_hubs?.length || 0} VoiceMaster hubs configured — manage with /voicemaster hub.
                </ReadOnlyNote>
              </ModuleCard>
            </FadeIn>

            <FadeIn>
              <GlassCard className="p-6 md:p-8" hover={false} data-testid="activity-card">
                <div id="activity" className="scroll-mt-40">
                  <h2 className="flex items-center gap-3 font-display text-xl font-semibold text-paper-100">
                    <ActivityIcon className="h-5 w-5" aria-hidden="true" />
                    Recent activity
                  </h2>
                  {data.activity.length === 0 ? (
                    <p className="mt-4 text-sm text-paper-500" data-testid="activity-empty">
                      No dashboard activity yet — changes you save here will appear in this feed.
                    </p>
                  ) : (
                    <ul className="mt-5 space-y-3" data-testid="activity-list">
                      {data.activity.map((entry, i) => (
                        <li key={entry.id || i} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                          <span className="text-sm text-paper-200">
                            <span className="font-mono text-xs text-paper-500">{entry.action}</span>
                            {entry.payload?.updated_keys && (
                              <span className="text-paper-400"> — {entry.payload.updated_keys.join(", ")}</span>
                            )}
                          </span>
                          <span className="font-mono text-xs text-paper-500">
                            {entry.created_at ? new Date(entry.created_at).toLocaleString() : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </GlassCard>
            </FadeIn>
          </div>
        </>
      )}
    </section>
  );
}

